import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { LoanDataExportTemplate, LoanDataExportTemplateId } from '@/types/loan-data-export';

interface TemplatePickerProps {
  templates: LoanDataExportTemplate[];
  selectedTemplateId: LoanDataExportTemplateId | null;
  onSelect: (templateId: LoanDataExportTemplateId) => void;
  loading?: boolean;
}

const TRIGGER_ID = 'loan-data-export-template';

export function TemplatePicker({
  templates,
  selectedTemplateId,
  onSelect,
  loading = false
}: TemplatePickerProps) {
  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId) ?? null;

  if (loading) {
    return (
      <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3" aria-label="Loading data tapes">
        <div className="h-4 w-20 animate-pulse rounded bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-lg bg-muted sm:w-[21rem]" />
      </div>
    );
  }

  if (templates.length === 0 || !selectedTemplate) {
    return (
      <p className="flex h-10 shrink-0 items-center text-sm text-muted-foreground">No data tapes are available.</p>
    );
  }

  return (
    <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
      <label htmlFor={TRIGGER_ID} className="shrink-0 text-sm font-medium">
        Data tape
      </label>
      <Select
        value={selectedTemplate.id}
        onValueChange={(value) => onSelect(value as LoanDataExportTemplateId)}
      >
        <SelectTrigger id={TRIGGER_ID} className="h-10 w-full min-w-0 bg-card sm:w-[21rem]">
          <SelectValue>{selectedTemplate.name}</SelectValue>
        </SelectTrigger>
        <SelectContent className="max-w-[calc(100vw-1rem)]">
          {templates.map((template) => (
            <SelectItem key={template.id} value={template.id} textValue={template.name}>
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{template.name}</span>
                <span className="text-xs text-muted-foreground">
                  {template.vendor}, {template.columns.length} {template.columns.length === 1 ? 'column' : 'columns'}, version {template.version}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedTemplate.containsSensitiveData ? (
        <span className="text-xs font-medium text-warning-strong">Includes SSNs</span>
      ) : null}
    </div>
  );
}
